<template>
  <div>
    <q-checkbox
      class="widget-field-checkbox"
      :class="{
        'widget-field-checkbox--align-top': alignTop,
        'widget-field-checkbox--error': hasError,
      }"
      :model-value="modelValue"
      @update:model-value="updateValue"
    >
      <slot />
    </q-checkbox>
    <widget-field-error :text="errorMessage" />
  </div>
</template>

<script setup lang="ts">
import type { WidgetFieldEmits, WidgetFieldProps } from '@widgets/shared/components/fields/types'
import WidgetFieldError from '@widgets/shared/components/fields/WidgetFieldError.vue'

export type WidgetFieldCheckboxExtraProps = {
  alignTop?: boolean
}

defineProps<WidgetFieldProps<boolean> & WidgetFieldCheckboxExtraProps>()
const emit = defineEmits<WidgetFieldEmits<boolean>>()

const updateValue = (value: boolean) => {
  emit('update:model-value', value)
  emit('blur')
}
</script>

<style lang="scss" scoped>
.widget-field-checkbox {
  display: flex;

  &--align-top {
    align-items: flex-start;
    :deep(.q-checkbox__inner) {
      margin-top: 1px;
    }
  }

  &--error {
    :deep(.q-checkbox__inner) {
      border-color: $red !important;
    }
  }

  &:deep(.q-checkbox__inner) {
    font-size: $xm-font;
    background-color: $white;
    border: 1px solid $border-button;
    border-radius: $border-radius;
    transition: all $transition-300ms-ease-in-out;
    box-shadow: 0 2px 4px rgba($black, 0.05);
    &:hover {
      background: $shade;
    }

    // hide ripple animation
    &::before {
      content: none !important;
    }

    // icon background
    .q-checkbox__bg {
      top: 50%;
      left: 50%;
      width: 0.7em;
      height: 0.7em;
      transform: translate(-50%, -50%);
      border: 0;
      background-color: transparent;
    }

    // color on active states
    &.q-checkbox__inner--truthy,
    &.q-checkbox__inner--indet {
      background-color: $blue;
      border-color: $blue;
      &:hover {
        background-color: $blue600;
        border-color: $blue600;
      }
    }
  }

  &:deep(.q-checkbox__label) {
    min-width: 0;
  }

  &:deep(.q-checkbox__label):not(:empty) {
    font-size: $font-size-root;
    font-weight: $semi-bold-font;
    margin-left: 8px;
    line-height: 20px;
  }
}
</style>
